import { IHeaderModalActions } from "@/common/header-modal/header-modal-actions";
import { ILandingActions } from "@/landing/landing-actions";
import { sendWarningMessage } from "@/utils/slack";
import { defined } from "@/utils/variable-evaluation";
import { createReducer } from "@reduxjs/toolkit";
import type { IGetSubscriptionsConfig } from "./account-action-creators";
import { IAccountActions } from "./account-actions";
import type {
  IAutoMessageCategory,
  ICreditCard,
  IIdVerificationInfo,
  IMyPurchase,
  IProviderConfig,
  ISubscriptionInfo,
  IUserInfo,
  IVaultMedia,
} from "./account-models";
import { EAutoMessageType, ESubscriptionInfoStatus } from "./account-models";

export interface IAccountModel {
  user: IUserInfo | null;
  providerConfig: IProviderConfig | null;
  savedCreditCards: ICreditCard[];
  nextSubscriptionsUrl: string | null;
  subscriptions: ISubscriptionInfo[] | null;
  subscriptionsTotal: number;
  isManageSubscriptionsEditing: boolean;
  manageSubscriptionsSelection: ISubscriptionInfo[];
  autoMessageCategories: IAutoMessageCategory[];
  lastSavedAutoMessageCategories: IAutoMessageCategory[];
  nextMyPurchasesUrl: string;
  myPurchases: IMyPurchase[];
  idVerification: IIdVerificationInfo | null;
  nextGetVaultMediaUrl: string;
  vaultMedia: IVaultMedia[];
}

const INITIAL_STATE: IAccountModel = {
  user: null,
  providerConfig: null,
  savedCreditCards: [],
  nextSubscriptionsUrl: null,
  subscriptions: null,
  subscriptionsTotal: 0,
  isManageSubscriptionsEditing: false,
  manageSubscriptionsSelection: [],
  autoMessageCategories: [],
  lastSavedAutoMessageCategories: [],
  nextMyPurchasesUrl: "",
  myPurchases: [],
  idVerification: null,
  nextGetVaultMediaUrl: "",
  vaultMedia: [],
};

/** Reducer */
export const accountReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(
      IAccountActions.resetUpdateUserNotifications,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.resetUpdateUserProfile,
      (state, action): IAccountModel => {
        return state;
      }
    )
    // .addCase(IAccountActions.setUser, (state, action): IAccountModel => {
    //   return {
    //     ...state,
    //     user: action.payload.user
    //   };
    // })
    .addCase(IAccountActions.initGetUser, (state, action): IAccountModel => {
      const { isForceLoader } = action.payload;
      return {
        ...state,
        user: isForceLoader ? null : state.user,
      };
    })
    .addCase(IAccountActions.successGetUser, (state, action): IAccountModel => {
      return {
        ...state,
        user: action.payload,
      };
    })
    .addCase(IAccountActions.failureGetUser, (state, action): IAccountModel => {
      return state;
    })
    .addCase(
      IAccountActions.initGetProviderConfig,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successGetProviderConfig,
      (state, action): IAccountModel => {
        const providerConfig: IProviderConfig = action.payload;
        return {
          ...state,
          providerConfig,
        };
      }
    )
    .addCase(
      IAccountActions.failureGetProviderConfig,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.initGetUsernameAvailable,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successGetUsernameAvailable,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.failureGetUsernameAvailable,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.resetPatchUpdateUser,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.initPatchUpdateUser,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successPatchUpdateUser,
      (state, action): IAccountModel => {
        return {
          ...state,
          user: action.payload.res, // updating user object here!
        };
      }
    )
    .addCase(
      IAccountActions.failurePatchUpdateUser,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.initPostGetCredits,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successPostGetCredits,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.failurePostGetCredits,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.resetPostGetCredits,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.initDeleteUserLogout,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successDeleteUserLogout,
      (state, action): IAccountModel => {
        return {
          ...state,
          user: null,
        };
      }
    )
    .addCase(
      IAccountActions.errorDeleteUserLogout,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(IAccountActions.updateUser, (state, action): IAccountModel => {
      const { newValues } = action.payload;
      let user = state.user;

      if (defined(user)) {
        user = { ...user, ...newValues } as IUserInfo;
      }
      return { ...state, user };
    })
    .addCase(
      IAccountActions.updateProviderConfig,
      (state, action): IAccountModel => {
        const { newValues } = action.payload;
        let providerConfig = state.providerConfig;

        if (defined(providerConfig)) {
          providerConfig = { ...providerConfig, ...newValues };
        }
        return { ...state, providerConfig };
      }
    )
    .addCase(
      IAccountActions.initGetSavedCreditCards,
      (state, action): IAccountModel => {
        return {
          ...state,
          savedCreditCards: [],
        };
      }
    )
    .addCase(
      IAccountActions.successGetSavedCreditCards,
      (state, action): IAccountModel => {
        return {
          ...state,
          savedCreditCards: action.payload.data,
        };
      }
    )
    .addCase(
      IHeaderModalActions.chatFavorited,
      (state, action): IAccountModel => {
        return {
          ...state,
          user: {
            ...state.user!,
            has_favorites: true,
          },
        };
      }
    )
    .addCase(IAccountActions.initGetSubscriptions, (state): IAccountModel => {
      return state;
    })
    .addCase(
      IAccountActions.successGetSubscriptions,
      (state, action): IAccountModel => {
        const config: IGetSubscriptionsConfig = action.payload.config;
        const res = action.payload.res;

        let subscriptions: ISubscriptionInfo[] = res.data;
        const getSubscriptionsNextUrl: string =
          res.data.length > 0 ? res.next : "";
        const subscriptionsTotal: number = res.total;

        if (!config.isInitialRequest) {
          subscriptions = [...(state.subscriptions || []), ...res.data];
        }

        return {
          ...state,
          nextSubscriptionsUrl: getSubscriptionsNextUrl,
          subscriptions,
          subscriptionsTotal,
        };
      }
    )
    .addCase(
      IAccountActions.setManageSubscriptionsEditing,
      (state, action): IAccountModel => {
        return {
          ...state,
          isManageSubscriptionsEditing: action.payload,
          manageSubscriptionsSelection: [], // reset
        };
      }
    )
    .addCase(
      IAccountActions.toggleManageSubscriptionsSubSelection,
      (state, action): IAccountModel => {
        const selectedSub: ISubscriptionInfo = action.payload;
        let manageSubscriptionsSelection: ISubscriptionInfo[];
        if (
          state.manageSubscriptionsSelection.findIndex(
            (sub) => sub.user._id === selectedSub.user._id
          ) !== -1
        ) {
          manageSubscriptionsSelection =
            state.manageSubscriptionsSelection.filter(
              (sub) => sub.user._id !== selectedSub.user._id
            );
        } else {
          manageSubscriptionsSelection = [
            ...state.manageSubscriptionsSelection,
            selectedSub,
          ];
        }
        return {
          ...state,
          manageSubscriptionsSelection,
        };
      }
    )
    .addCase(
      IAccountActions.toggleManageSubscriptionsAllSubsSelection,
      (state, action): IAccountModel => {
        const manageSubscriptionsSelection = (state.subscriptions || []).filter(
          (sub) => sub.status === ESubscriptionInfoStatus.ACTIVE
        );
        return {
          ...state,
          manageSubscriptionsSelection,
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteSubscriptions,
      (state, action): IAccountModel => {
        return {
          ...state,
          isManageSubscriptionsEditing: false, // reset
          manageSubscriptionsSelection: [], // reset
        };
      }
    )
    .addCase(
      IAccountActions.successPatchReenableSubscription,
      (state, action): IAccountModel => {
        const updatedSub: ISubscriptionInfo = action.payload.data[0];
        return {
          ...state,
          subscriptions: (state.subscriptions || []).map((sub) =>
            sub.user._id === updatedSub.user._id ? updatedSub : sub
          ),
        };
      }
    )
    .addCase(
      IAccountActions.successPatchUserRandomAvatar,
      (state, action): IAccountModel => {
        return {
          ...state,
          user: action.payload || state.user,
        };
      }
    )
    .addCase(
      IAccountActions.initGetAutoMessages,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successGetAutoMessages,
      (state, action): IAccountModel => {
        const autoMessageCategories: IAutoMessageCategory[] =
          action.payload.data;

        // reporting of missing dimensions
        // (TODO: consider removing this later to improve performance)
        autoMessageCategories.forEach((category) => {
          category.messages.forEach((autoMessage) => {
            if (
              autoMessage.type === EAutoMessageType.Image ||
              autoMessage.type === EAutoMessageType.Video
            ) {
              if (!defined(autoMessage.media)) {
                sendWarningMessage(
                  `AutoMessage type \`${autoMessage.type}\` fetched but without media! \`${autoMessage._id}\``
                );
              } else if (
                !defined(autoMessage.media!.width) ||
                !defined(autoMessage.media!.height)
              ) {
                sendWarningMessage(
                  `AutoMessage fetched with media but without dimensions! \`${autoMessage._id}\``
                );
              }
            }
          });
        });

        return {
          ...state,
          autoMessageCategories,
          lastSavedAutoMessageCategories: autoMessageCategories, // replace with new
        };
      }
    )
    .addCase(
      IAccountActions.failureGetAutoMessages,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.initPostAutoMessage,
      (state, action): IAccountModel => {
        const { category, clientSideAutoMessage } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // add clientSideAutoMessage
              return {
                ...c,
                messages: [...c.messages, clientSideAutoMessage],
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.updatePostAutoMessageProgress,
      (state, action): IAccountModel => {
        const { category, progress, loaded, total, __clientSideSendId } =
          action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // update clientSideAutoMessage with new progress value
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m.__clientSideSendId === __clientSideSendId) {
                    return {
                      ...m,
                      media: {
                        ...m.media,
                        uploadProgress: progress,
                        loaded,
                        total,
                      },
                    };
                  }
                  return m;
                }),
              };
            }
            return c;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- This heterogeneous external event/action payload needs an audited discriminated model.
          }) as any,
        };
      }
    )
    .addCase(
      IAccountActions.successPostAutoMessage,
      (state, action): IAccountModel => {
        const { category, response, __clientSideSendId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // replace clientSideAutoMessage with API response
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m.__clientSideSendId === __clientSideSendId) {
                    return response.data;
                  }
                  return m;
                }),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.failurePostAutoMessage,
      (state, action): IAccountModel => {
        const { category, isAbort, errorResponse, __clientSideSendId } =
          action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              if (isAbort) {
                // remove clientSideAutoMessage
                return {
                  ...c,
                  messages: c.messages.filter(
                    (m) => m.__clientSideSendId !== __clientSideSendId
                  ),
                };
              } else {
                // update clientSideAutoMessage
                return {
                  ...c,
                  messages: c.messages.map((m) => {
                    if (m.__clientSideSendId === __clientSideSendId) {
                      return {
                        ...m,
                        __isUploadFailed: true,
                        __errorMessage:
                          (errorResponse && errorResponse.message) || "",
                      };
                    }
                    return m;
                  }),
                };
              }
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteAutoMessage,
      (state, action): IAccountModel => {
        const { category, autoMessageId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // mark the message to be deleted as __isBeingDeleted
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m._id === autoMessageId) {
                    return {
                      ...m,
                      __isBeingDeleted: true,
                    };
                  }
                  return m;
                }),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteAutoMessage,
      (state, action): IAccountModel => {
        const { category, autoMessageId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // remove deleted message
              return {
                ...c,
                messages: c.messages.filter((m) => m._id !== autoMessageId),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteAutoMessage,
      (state, action): IAccountModel => {
        const { category, errorResponse, autoMessageId } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              return {
                ...c,
                messages: c.messages.map((m) => {
                  if (m._id === autoMessageId) {
                    return {
                      ...m,
                      __isBeingDeleted: false,
                      __errorMessage:
                        (errorResponse && errorResponse.message) || "",
                    };
                  }
                  return m;
                }),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.initPutAutoMessages,
      (state, action): IAccountModel => {
        const { category } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // mark all messages in this category as __isBeingDeleted
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: true,
                })),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.successPutAutoMessages,
      (state, action): IAccountModel => {
        const { category } = action.payload;
        const autoMessageCategories = state.autoMessageCategories.map((c) => {
          if (c.category === category) {
            // false the loading indicator for all messages in this category (__isBeingDeleted)
            return {
              ...c,
              messages: c.messages.map((m) => ({
                ...m,
                __isBeingDeleted: false,
              })),
            };
          }
          return c;
        });
        return {
          ...state,
          autoMessageCategories,
          lastSavedAutoMessageCategories: autoMessageCategories,
        };
      }
    )
    .addCase(
      IAccountActions.failurePutAutoMessages,
      (state, action): IAccountModel => {
        const { category, errorResponse } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: false,
                  __errorMessage:
                    (errorResponse && errorResponse.message) || "",
                })),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.initDeleteAutoMessages,
      (state, action): IAccountModel => {
        const { category } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              // mark all messages in this category as __isBeingDeleted
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: true,
                })),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteAutoMessages,
      (state, action): IAccountModel => {
        const { category } = action.payload;
        const autoMessageCategories = state.autoMessageCategories.map((c) => {
          if (c.category === category) {
            // empty this category
            return {
              ...c,
              excerpt: "",
              messages: [],
            };
          }
          return c;
        });
        return {
          ...state,
          autoMessageCategories,
          lastSavedAutoMessageCategories: autoMessageCategories,
        };
      }
    )
    .addCase(
      IAccountActions.failureDeleteAutoMessages,
      (state, action): IAccountModel => {
        const { category, errorResponse } = action.payload;
        return {
          ...state,
          autoMessageCategories: state.autoMessageCategories.map((c) => {
            if (c.category === category) {
              return {
                ...c,
                messages: c.messages.map((m) => ({
                  ...m,
                  __isBeingDeleted: false,
                  __errorMessage:
                    (errorResponse && errorResponse.message) || "",
                })),
              };
            }
            return c;
          }),
        };
      }
    )
    .addCase(
      IAccountActions.resetAutoMessages,
      (state, action): IAccountModel => {
        return {
          ...state,
          autoMessageCategories: state.lastSavedAutoMessageCategories, // replace with old
        };
      }
    )
    .addCase(
      IAccountActions.initPostUserTag,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.successPostUserTag,
      (state, action): IAccountModel => {
        const { tag } = action.payload;
        return {
          ...state,
          user:
            state.user === null
              ? null
              : {
                  ...state.user,
                  tags: [...(state.user.tags || []), tag],
                },
        };
      }
    )
    .addCase(
      IAccountActions.failurePostUserTag,
      (state, action): IAccountModel => {
        return state;
      }
    )
    .addCase(
      IAccountActions.initGetMyPurchases,
      (state, action): IAccountModel => {
        return {
          ...state,
          myPurchases: [],
        };
      }
    )
    .addCase(
      IAccountActions.successGetMyPurchases,
      (state, action): IAccountModel => {
        return {
          ...state,
          myPurchases: action.payload.data,
          nextMyPurchasesUrl: action.payload.next,
        };
      }
    )
    .addCase(
      IAccountActions.successGetMyPurchasesNext,
      (state, action): IAccountModel => {
        return {
          ...state,
          myPurchases: [...state.myPurchases, ...action.payload.data],
          nextMyPurchasesUrl: action.payload.next,
        };
      }
    )
    .addCase(
      IAccountActions.successPatchHideMyPurchase,
      (state, action): IAccountModel => {
        const { messageIds } = action.payload;
        return {
          ...state,
          myPurchases: (state.myPurchases || []).filter(
            (purchase) => !messageIds.includes(purchase.message_id)
          ),
        };
      }
    )
    .addCase(
      IAccountActions.successPatchUnhideMyPurchase,
      (state, action): IAccountModel => {
        const { messageIds } = action.payload;
        return {
          ...state,
          myPurchases: (state.myPurchases || []).filter(
            (purchase) => !messageIds.includes(purchase.message_id)
          ),
        };
      }
    )
    .addCase(
      IAccountActions.removeExpiredFromMyPurchases,
      (state, action): IAccountModel => {
        const myPurchases = state.myPurchases.filter(
          (purchase) => purchase.message_id !== action.payload.messageId
        );
        return {
          ...state,
          myPurchases,
        };
      }
    )
    .addCase(
      IAccountActions.successGetIdVerification,
      (state, action): IAccountModel => {
        return {
          ...state,
          idVerification: action.payload,
        };
      }
    )
    .addCase(
      IAccountActions.initGetVaultMedia,
      (state, action): IAccountModel => {
        return {
          ...state,
          vaultMedia: [],
        };
      }
    )
    .addCase(
      IAccountActions.successGetVaultMedia,
      (state, action): IAccountModel => {
        return {
          ...state,
          vaultMedia: action.payload.data || [],
          nextGetVaultMediaUrl: action.payload.next || "",
        };
      }
    )
    .addCase(
      IAccountActions.successGetVaultMediaNext,
      (state, action): IAccountModel => {
        return {
          ...state,
          vaultMedia: [...state.vaultMedia, ...action.payload.data],
          nextGetVaultMediaUrl: action.payload.next,
        };
      }
    )
    .addCase(
      IAccountActions.successDeleteVaultMedia,
      (state, action): IAccountModel => {
        return {
          ...state,
          vaultMedia: (state.vaultMedia || []).filter(
            (media) => !action.payload.mediaIds.includes(media._id)
          ),
        };
      }
    )
    .addCase(ILandingActions.imageDeleted, (state, action): IAccountModel => {
      const { public_id, media_id } = action.payload;

      if (state.user && state.user.featured) {
        const updatedFeatured = state.user.featured.filter((img) => {
          if (public_id && img.public_id === public_id) return false;
          if (media_id && img.media_id === media_id) return false;
          return true;
        });

        return {
          ...state,
          user: {
            ...state.user,
            featured: updatedFeatured,
          },
        };
      }

      return state;
    });
});
